import React, { useEffect } from 'react';
import { formatChange } from '../utils';

const TradeTab = ({
  // 검색 관련
  searchKeyword,
  setSearchKeyword,
  searchResults,
  setSearchResults,
  showSearchResults,
  setShowSearchResults,
  isSearching,
  setIsSearching,
  
  // 종목 선택 관련
  selectedStock,
  setSelectedStock,
  stockData,
  setStockData,
  loading,
  setLoading,
  refreshingName,
  setRefreshingName,
  
  // 주문 관련
  orderType,
  setOrderType,
  quantity,
  setQuantity,
  price,
  setPrice,
  orderMethod,
  setOrderMethod,
  
  // 즐겨찾기 관련
  favoriteStocks,
  setFavoriteStocks,
  fetchFavoriteStocks,
  showFavoriteStockModal,
  setShowFavoriteStockModal,
  
  // 관심종목 관련
  watchlist,
  fetchWatchlist,
  
  // 종목 조회 드롭다운 관련
  popularStocksTab,
  setPopularStocksTab,
  popularStocksDropdownOpen,
  setPopularStocksDropdownOpen,
  portfolioDropdownOpen,
  setPortfolioDropdownOpen,
  popularStocksList,
  setPopularStocksList,
  aiRecommendedStocksList,
  setAiRecommendedStocksList,
  watchlistStocksList,
  setWatchlistStocksList,
  
  // 포트폴리오 관련
  portfolio,
  fetchPortfolio,
  
  // AI 분석 관련
  aiAnalysis,
  setAiAnalysis,
  buildAnalysisMetadata,
  aiAnalyses,
  aiQuestion,
  setAiQuestion,
  aiAnswer,
  setAiAnswer,
  aiQuestionLoading,
  setAiQuestionLoading,
  simpleExplanation,
  setSimpleExplanation,
  explanationLoading,
  setExplanationLoading,
  itemExplanations,
  setItemExplanations,
  itemExplanationLoading,
  setItemExplanationLoading,
  
  // 함수들
  authenticatedFetch,
  fetchStockData,
  executeOrder,
  requestAiAnalysis,
  updateAiAnalysis,
  fetchAiAnalyses,
  askAiQuestion,
  generateSimpleExplanation,
  generateItemExplanation,
  resetAiAnalysis,
  refreshStockName,
  setSelectedAnalysis,
  setShowAnalysisDetail,
  setActiveTab,
  
  // AI 분석 옵션
  aiAnalysisOptions,
  setAiAnalysisOptions,
  
  // 기타
  authError,
  defaultPopularStocks
}) => {
  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    if (!popularStocksDropdownOpen && !portfolioDropdownOpen) return;
    
    const handleClickOutside = (event) => {
      const popularStocksElements = document.querySelectorAll('.popular-stocks');
      let clickedInside = false;
      popularStocksElements.forEach(element => {
        if (element && element.contains(event.target)) {
          clickedInside = true;
        }
      });
      
      if (!clickedInside) {
        setPopularStocksDropdownOpen(false);
        setPortfolioDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [popularStocksDropdownOpen, portfolioDropdownOpen, setPopularStocksDropdownOpen, setPortfolioDropdownOpen]);

  // AI 분석 결과 자동 연동 (selectedStock 또는 aiAnalyses 변경 시)
  useEffect(() => {
    // 종목이 선택되지 않았으면 초기화
    if (!selectedStock || !selectedStock.trim()) {
      if (aiAnalysis) {
        setAiAnalysis(null);
      }
      return;
    }

    // aiAnalyses가 없거나 비어있으면 fetchAiAnalyses 호출 시도
    if (!aiAnalyses || aiAnalyses.length === 0) {
      console.log('ℹ️ AI 분석 결과 없음, 조회 시도:', selectedStock);
      if (fetchAiAnalyses && typeof fetchAiAnalyses === 'function') {
        fetchAiAnalyses().then(() => {
          console.log('✅ AI 분석 결과 조회 완료');
        }).catch(err => {
          console.warn('⚠️ AI 분석 결과 조회 실패:', err);
        });
      }
      // 조회 중이므로 일단 초기화하지 않음 (조회 완료 후 다시 체크)
      return;
    }

    // 선택된 종목에 대한 AI 분석 결과 찾기 (종목코드 정확히 일치)
    const currentAnalysis = aiAnalyses.find(a => {
      const analysisSymbol = (a.symbol || '').toString().trim();
      const selectedSymbol = (selectedStock || '').toString().trim();
      return analysisSymbol === selectedSymbol;
    });
    
    if (currentAnalysis) {
      // AI 분석 데이터 자동 로드 (항상 최신 분석 결과로 업데이트)
      const recommendation = currentAnalysis.recommendation;
      const safeRecommendation = (recommendation && typeof recommendation === 'object') 
        ? recommendation 
        : (recommendation || null);
      
      // 분석 일자 정보 추가
      const analysisDate = currentAnalysis.createdAt 
        ? new Date(currentAnalysis.createdAt).toLocaleString('ko-KR')
        : currentAnalysis.updatedAt 
          ? new Date(currentAnalysis.updatedAt).toLocaleString('ko-KR')
          : null;
      
      const stockName = stockData?.name || stockData?.stock_name || selectedStock || '';
      const metadata = (typeof buildAnalysisMetadata === 'function')
        ? buildAnalysisMetadata(currentAnalysis, selectedStock, stockName)
        : { tools: ['규칙 기반'], scope: { 종목코드: selectedStock, 종목명: stockName, 분석유형: '단일 종목' }, confidence: safeRecommendation?.confidence ?? 0 };
      setAiAnalysis({
        recommendation: safeRecommendation,
        analysis: currentAnalysis.detailed_analysis || currentAnalysis.summary || '',
        analysisMetadata: metadata,
        confidence: safeRecommendation?.confidence || safeRecommendation?.confidence_score || 0,
        analysis_type: currentAnalysis.analysis_type || 'combined',
        local_ai: currentAnalysis.local_ai_analysis,
        google_ai: currentAnalysis.google_ai_analysis,
        current_status: currentAnalysis.current_status,
        trade_config: currentAnalysis.trade_config,
        analysisDate: analysisDate,
        isExisting: true
      });
      
      console.log('✅ AI 분석 결과 자동 연동:', {
        symbol: selectedStock,
        analysisId: currentAnalysis._id,
        hasRecommendation: !!safeRecommendation,
        hasAnalysis: !!(currentAnalysis.detailed_analysis || currentAnalysis.summary),
        analysisDate: analysisDate,
        recommendationAction: safeRecommendation?.action
      });
    } else {
      // 현재 종목에 대한 AI 분석이 없으면 초기화
      if (aiAnalysis) {
        setAiAnalysis(null);
        console.log('ℹ️ AI 분석 결과 없음:', selectedStock, {
          availableSymbols: aiAnalyses.map(a => a.symbol).slice(0, 5)
        });
      }
    }
  }, [selectedStock, aiAnalyses, setAiAnalysis, aiAnalysis, fetchAiAnalyses, buildAnalysisMetadata, stockData]);

  return (
    <div className="trading-layout">
      {/* 왼쪽: 주식 선택 및 주문 */}
      <div className="trading-sidebar">
        <div className="stock-selector">
          <h2>종목 선택</h2>
          
          {/* 종목 검색 */}
          <div className="stock-search">
            <input
              type="text"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              placeholder="종목명 또는 종목코드 검색 (예: 삼성전자, 005930)"
              className="stock-search-input"
            />
            {isSearching && <span className="search-loading">검색 중...</span>}
            
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((stock, index) => (
                  <div
                    key={index}
                    className="search-result-item"
                    onClick={async () => {
                      setSelectedStock(stock.symbol);
                      // 종목명도 함께 저장하여 즉시 표시
                      const stockName = stock.name || stock.stock_name || '';
                      
                      // stockData 초기화 (종목 선택 시 기존 데이터 초기화)
                      setStockData({
                        symbol: stock.symbol,
                        name: stockName || '',
                        stock_name: stockName || '',
                        price: stock.price || 0,
                        change: 0,
                        changeRate: 0,
                        changePercent: 0,
                        volume: 0,
                        open: 0,
                        high: 0,
                        low: 0
                      });
                      
                      setSearchKeyword(stockName ? `${stock.symbol} - ${stockName}` : stock.symbol);
                      setShowSearchResults(false);
                      
                      // 종목 선택 후 자동으로 주식 데이터 조회 (가격 정보 및 종목명 업데이트)
                      if (fetchStockData) {
                        try {
                          // 약간의 지연 후 조회 (상태 업데이트 완료 대기)
                          setTimeout(async () => {
                            try {
                              await fetchStockData(stock.symbol, { force: false });
                              
                              // 종목명이 없으면 추가로 종목명 갱신 시도
                              if (!stockName) {
                                setTimeout(async () => {
                                  try {
                                    const refreshResponse = await authenticatedFetch(`/api/trading/stock/${stock.symbol}/refresh-name`, {
                                      method: 'POST',
                                      timeout: 30000
                                    });
                                    
                                    if (refreshResponse.ok) {
                                      const refreshData = await refreshResponse.json();
                                      if (refreshData.success && refreshData.name) {
                                        console.log('✅ 종목명 자동 갱신 성공:', refreshData.name);
                                        // 종목명 갱신 후 stockData 업데이트
                                        setStockData(prev => ({
                                          ...prev,
                                          name: refreshData.name,
                                          stock_name: refreshData.name
                                        }));
                                      }
                                    }
                                  } catch (refreshError) {
                                    console.warn('⚠️ 종목명 자동 갱신 실패:', refreshError);
                                  }
                                }, 300);
                              }
                            } catch (error) {
                              console.warn('종목 데이터 조회 실패:', error);
                              // 조회 실패 시에도 기본 정보는 표시됨 (검색 결과에서 얻은 정보)
                            }
                          }, 100);
                        } catch (error) {
                          console.warn('종목 데이터 조회 실패:', error);
                          // 조회 실패는 무시 (이미 검색 결과에서 정보를 얻었으므로)
                        }
                      }
                      
                      // 종목 검색 후 자동 저장 (즐겨찾기에 없으면 추가)
                      try {
                        const existing = favoriteStocks.find(fs => fs.symbol === stock.symbol);
                        if (!existing && stockName) {
                          const addResponse = await authenticatedFetch('/api/user-favorite-stocks', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              symbol: stock.symbol,
                              stock_name: stockName,
                              market: stock.market || 'KOSPI/KOSDAQ',
                              source: 'search'
                            })
                          });
                          
                          if (addResponse.ok) {
                            console.log(`✅ 검색 종목 자동 저장: ${stock.symbol}`);
                            if (fetchFavoriteStocks) {
                              await fetchFavoriteStocks();
                            }
                          }
                        }
                      } catch (error) {
                        console.error('검색 종목 자동 저장 실패:', error);
                        // 자동 저장 실패는 무시 (사용자 경험에 영향 없음)
                      }
                    }}
                  >
                    <div className="result-symbol">{stock.symbol}</div>
                    <div className="result-name">{stock.name || stock.stock_name || '종목명 없음'}</div>
                    <div className="result-market">{stock.market || ''}</div>
                    {stock.price > 0 && (
                      <div className="result-price" style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        ₩{parseInt(stock.price).toLocaleString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {showSearchResults && searchKeyword && searchResults.length === 0 && !isSearching && (
              <div className="search-results">
                <div className="search-no-results">
                  <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    <div style={{ marginBottom: '8px' }}>검색 결과가 없습니다.</div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      종목코드(6자리) 또는 종목명으로 검색해주세요.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 인기/추천/관심항목 목록 - 탭 기반 드롭다운 */}
          {/* 보유 종목 목록 (인기 종목 위로 이동) */}
          <div className="popular-stocks" style={{ position: 'relative', zIndex: portfolioDropdownOpen ? 1000 : 'auto', marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ margin: 0 }}>💼 보유종목</h3>
              <button
                onClick={async () => {
                  await fetchPortfolio();
                }}
                style={{
                  padding: '4px 8px',
                  fontSize: '11px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
                title="새로고침"
              >
                🔄
              </button>
            </div>
            
            {/* 드롭다운 트리거 */}
            <button
              onClick={() => setPortfolioDropdownOpen(!portfolioDropdownOpen)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                textAlign: 'left'
              }}
            >
              <span>보유종목에서 선택</span>
              <span style={{ transform: portfolioDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
            </button>

            {/* 드롭다운 컨텐츠 */}
            {portfolioDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                zIndex: 1000,
                maxHeight: '300px',
                overflowY: 'auto',
                padding: '8px'
              }}>
                {!portfolio || !Array.isArray(portfolio) || portfolio.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    보유 종목이 없습니다.
                  </div>
                ) : (
                  portfolio.map((item) => (
                    <div
                      key={item.symbol || item._id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px',
                        borderBottom: '1px solid #eee',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        const target = e.currentTarget;
                        if (target) target.style.backgroundColor = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        const target = e.currentTarget;
                        if (target) target.style.backgroundColor = 'white';
                      }}
                      onClick={async () => {
                        if (item.symbol) {
                        setSelectedStock(item.symbol);
                        setSearchKeyword('');
                        setShowSearchResults(false);
                        setPortfolioDropdownOpen(false);
                          // 종목 데이터 조회
                          await fetchStockData(item.symbol);
                        }
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500' }}>
                          {item.symbol} - {item.name || item.stock_name || 'N/A'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          보유: {item.quantity || 0}주
                          {item.current_price && (
                            <span style={{ marginLeft: '8px' }}>
                              현재가: ₩{item.current_price.toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="popular-stocks" style={{ position: 'relative', zIndex: popularStocksDropdownOpen ? 1000 : 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ margin: 0 }}>종목조회</h3>
              <div style={{ display: 'flex', gap: '5px' }}>
                <button
                  onClick={() => setShowFavoriteStockModal(true)}
                  style={{
                    padding: '4px 8px',
                    fontSize: '11px',
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                  title="종목 관리"
                >
                  ⚙️ 관리
                </button>
                <button
                  onClick={async () => {
                    await fetchFavoriteStocks();
                    await fetchWatchlist();
                  }}
                  style={{
                    padding: '4px 8px',
                    fontSize: '11px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                  title="새로고침"
                >
                  🔄
                </button>
              </div>
            </div>
            
            {/* 드롭다운 트리거 */}
            <button
              onClick={() => setPopularStocksDropdownOpen(!popularStocksDropdownOpen)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                textAlign: 'left'
              }}
            >
              <span>종목조회에서 선택</span>
              <span style={{ transform: popularStocksDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
            </button>

            {/* 드롭다운 컨텐츠 */}
            {popularStocksDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                zIndex: 1000,
                maxHeight: '400px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* 탭 메뉴 */}
                <div style={{
                  display: 'flex',
                  borderBottom: '1px solid #ddd'
                }}>
                  <button
                    onClick={() => setPopularStocksTab('popular')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: 'none',
                      backgroundColor: popularStocksTab === 'popular' ? 'white' : '#f5f5f5',
                      cursor: 'pointer',
                      borderBottom: popularStocksTab === 'popular' ? '2px solid #007bff' : '2px solid transparent',
                      fontWeight: popularStocksTab === 'popular' ? 'bold' : 'normal'
                    }}
                  >
                    🔥 인기종목
                  </button>
                  <button
                    onClick={() => setPopularStocksTab('ai-recommend')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: 'none',
                      backgroundColor: popularStocksTab === 'ai-recommend' ? 'white' : '#f5f5f5',
                      cursor: 'pointer',
                      borderBottom: popularStocksTab === 'ai-recommend' ? '2px solid #007bff' : '2px solid transparent',
                      fontWeight: popularStocksTab === 'ai-recommend' ? 'bold' : 'normal'
                    }}
                  >
                    🤖 AI분석종목
                  </button>
                  <button
                    onClick={() => setPopularStocksTab('watchlist')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: 'none',
                      backgroundColor: popularStocksTab === 'watchlist' ? 'white' : '#f5f5f5',
                      cursor: 'pointer',
                      borderBottom: popularStocksTab === 'watchlist' ? '2px solid #007bff' : '2px solid transparent',
                      fontWeight: popularStocksTab === 'watchlist' ? 'bold' : 'normal'
                    }}
                  >
                    📝 관심종목
                  </button>
                </div>

                {/* 종목 리스트 */}
                <div style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  padding: '8px'
                }}>
                  {(() => {
                    let currentStocks = [];
                    if (popularStocksTab === 'popular') {
                      currentStocks = popularStocksList || [];
                    } else if (popularStocksTab === 'ai-recommend') {
                      currentStocks = aiRecommendedStocksList || [];
                    } else if (popularStocksTab === 'watchlist') {
                      currentStocks = watchlistStocksList || [];
                    }

                    // 안전 체크: currentStocks가 배열이 아니거나 undefined인 경우
                    if (!Array.isArray(currentStocks) || currentStocks.length === 0) {
                      return (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                          {popularStocksTab === 'watchlist' ? (
                            <>
                              <div>관심종목이 없습니다.</div>
                              <div style={{ fontSize: '12px', marginTop: '8px', color: '#666' }}>
                                관심종목을 추가하거나 AI 추천 종목을 확인해보세요.
                              </div>
                            </>
                          ) : popularStocksTab === 'ai-recommend' ? (
                            <>
                              <div>AI 추천 종목이 없습니다.</div>
                              <div style={{ fontSize: '12px', marginTop: '8px', color: '#666' }}>
                                AI 거래 설정에서 종목 발굴을 실행해보세요.
                              </div>
                            </>
                          ) : (
                            <>
                              <div>인기종목이 없습니다.</div>
                              <div style={{ fontSize: '12px', marginTop: '8px', color: '#666' }}>
                                즐겨찾기 종목을 추가하거나 기본 인기종목을 확인해보세요.
                              </div>
                            </>
                          )}
                        </div>
                      );
                    }

                    return currentStocks.map((stock) => (
                      <div
                        key={stock.symbol || stock._id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px',
                          borderBottom: '1px solid #eee',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          const target = e.currentTarget;
                          if (target) target.style.backgroundColor = '#f5f5f5';
                        }}
                        onMouseLeave={(e) => {
                          const target = e.currentTarget;
                          if (target) target.style.backgroundColor = 'white';
                        }}
                        onClick={async () => {
                          if (stock.symbol) {
                          setSelectedStock(stock.symbol);
                          setSearchKeyword('');
                          setShowSearchResults(false);
                          setPopularStocksDropdownOpen(false);
                            // 종목 데이터 조회
                            await fetchStockData(stock.symbol);
                          }
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500' }}>
                            {stock.symbol} - {stock.stock_name || stock.name || 'N/A'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {stock.market || ''}
                          </div>
                        </div>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm(`${stock.stock_name || stock.name || stock.symbol} (${stock.symbol})을(를) 삭제하시겠습니까?`)) {
                              return;
                            }
                            try {
                              let response;
                              if (popularStocksTab === 'popular') {
                                response = await authenticatedFetch(`/api/user-favorite-stocks/popular/${stock.symbol}`, {
                                  method: 'DELETE'
                                });
                              } else if (popularStocksTab === 'ai-recommend') {
                                response = await authenticatedFetch(`/api/user-favorite-stocks/${stock._id}`, {
                                  method: 'DELETE'
                                });
                              } else if (popularStocksTab === 'watchlist') {
                                response = await authenticatedFetch(`/api/watchlist/${stock._id}`, {
                                  method: 'DELETE'
                                });
                              }

                              if (response && response.ok) {
                                alert('삭제되었습니다.');
                                // 상태 초기화 후 재조회
                                setPopularStocksList([]);
                                setAiRecommendedStocksList([]);
                                setWatchlistStocksList([]);
                                await fetchFavoriteStocks();
                                await fetchWatchlist();
                              } else {
                                const errorData = response ? await response.json().catch(() => ({ error: '삭제 실패' })) : { error: '응답 없음' };
                                alert(errorData.error || '삭제에 실패했습니다.');
                              }
                            } catch (error) {
                              console.error('삭제 실패:', error);
                              alert('삭제 중 오류가 발생했습니다.');
                            }
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#999',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            fontSize: '18px',
                            transition: 'color 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.color = '#ff4444'}
                          onMouseLeave={(e) => e.target.style.color = '#999'}
                          title="삭제"
                        >
                          ✕
                        </button>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}
          </div>

        </div>

        {selectedStock && selectedStock.trim() !== '' && (
          <div className="stock-info">
            {/* 종목명 및 버튼 영역 - 항상 표시 */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px', 
              marginBottom: stockData ? '15px' : '0', 
              flexWrap: 'nowrap',
              width: '100%',
              minHeight: '40px',
              position: 'relative'
            }}>
              <h3 style={{ 
                margin: 0, 
                flex: '0 1 auto', 
                minWidth: '150px',
                maxWidth: 'calc(100% - 280px)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {(() => {
                  const symbol = stockData?.symbol || selectedStock;
                  
                  if (!symbol) {
                    return '종목 미선택';
                  }
                  
                  // 종목명이 있고 종목코드와 다를 때: "종목명 (종목코드)" 형식
                  if (stockData?.name && stockData.name !== stockData.symbol && stockData.name !== selectedStock) {
                    return `${stockData.name} (${symbol})`;
                  }
                  
                  // 종목명이 없거나 종목코드와 같을 때: "종목명 (종목코드)" 형식으로 표시하되 종목명은 조회 중 또는 없음 표시
                  if (!stockData?.name || stockData.name === symbol || stockData.name === selectedStock) {
                    // 종목명이 없고 stockData가 로딩 중이면 "종목명 조회 중 (종목코드)" 표시
                    if (!stockData?.name && stockData && loading) {
                      return `종목명 조회 중 (${symbol})`;
                    }
                    // 종목명이 없으면 "종목명 (종목코드)" 형식으로 표시 (종목명 부분은 비워두거나 종목코드만 표시)
                    // 하지만 사용자가 원하는 형식에 맞춰 "종목명 (종목코드)" 형식으로 표시
                    return `종목명 (${symbol})`;
                  }
                  
                  // 기본값: "종목명 (종목코드)" 형식
                  return `종목명 (${symbol})`;
                })()}
              </h3>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                flexWrap: 'nowrap', 
                flexShrink: 0,
                marginLeft: 'auto',
                minWidth: '260px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => {
                    console.log('현재가 갱신 버튼 클릭 (force):', selectedStock);
                    fetchStockData(selectedStock, { force: true });
                  }}
                  disabled={loading}
                  className="btn-refresh-price"
                  style={{
                    padding: '6px 10px',
                    fontSize: '13px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                    whiteSpace: 'nowrap',
                    minWidth: 'auto',
                    fontWeight: '600',
                    flexShrink: 0
                  }}
                  title="현재가 갱신"
                >
                  U
                </button>
                <a
                  href="https://stock.naver.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-naver-link"
                  style={{
                    padding: '6px 10px',
                    fontSize: '13px',
                    backgroundColor: '#17a2b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                    display: 'inline-block',
                    minWidth: 'auto',
                    textAlign: 'center',
                    fontWeight: '600',
                    flexShrink: 0
                  }}
                  title="네이버 페이 증권(베타)에서 보기 (새 창)"
                  onClick={() => console.log('네이버 페이 증권 링크 클릭:', selectedStock)}
                >
                  N
                </a>
                {(!stockData?.name || stockData.name === stockData.symbol || stockData.name === selectedStock) && (
                  <button
                    onClick={refreshStockName}
                    disabled={refreshingName || loading}
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: refreshingName || loading ? 'not-allowed' : 'pointer',
                      opacity: refreshingName || loading ? 0.6 : 1,
                      whiteSpace: 'nowrap',
                      fontWeight: '500',
                      flexShrink: 0
                    }}
                    title="네이버 금융에서 종목명 자동 검색"
                  >
                    {refreshingName ? '갱신 중...' : '종목명 갱신'}
                  </button>
                )}
                {/* 즐겨찾기 추가/삭제 버튼 */}
                {selectedStock && (
                  favoriteStocks.find(item => item.symbol === selectedStock) ? (
                    <button
                      onClick={async () => {
                        if (!window.confirm(`${selectedStock} (${stockData?.name || '종목명 없음'})을(를) 즐겨찾기에서 삭제하시겠습니까?`)) {
                          return;
                        }
                        try {
                          const favoriteStock = favoriteStocks.find(item => item.symbol === selectedStock);
                          const response = await authenticatedFetch(`/api/user-favorite-stocks/${favoriteStock._id}`, {
                            method: 'DELETE'
                          });
                          
                          if (response.ok) {
                            alert('즐겨찾기에서 삭제되었습니다.');
                            await fetchFavoriteStocks();
                          } else {
                            const error = await response.json();
                            alert(error.error || '즐겨찾기 삭제에 실패했습니다.');
                          }
                        } catch (error) {
                          console.error('즐겨찾기 삭제 실패:', error);
                          alert('즐겨찾기 삭제 중 오류가 발생했습니다.');
                        }
                      }}
                      style={{
                        padding: '6px 10px',
                        fontSize: '13px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        fontWeight: '600',
                        flexShrink: 0
                      }}
                      title="즐겨찾기에서 삭제"
                    >
                      L
                    </button>
                  ) : (
                    <button
                      onClick={async () => {
                        try {
                          const response = await authenticatedFetch('/api/user-favorite-stocks', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              symbol: selectedStock,
                              stock_name: stockData?.name || '',
                              market: stockData?.market || 'KOSPI/KOSDAQ',
                              source: 'manual'
                            })
                          });
                          
                          if (response.ok) {
                            alert('즐겨찾기에 추가되었습니다.');
                            await fetchFavoriteStocks();
                          } else {
                            const error = await response.json();
                            alert(error.error || '즐겨찾기 추가에 실패했습니다.');
                          }
                        } catch (error) {
                          console.error('즐겨찾기 추가 실패:', error);
                          alert('즐겨찾기 추가 중 오류가 발생했습니다.');
                        }
                      }}
                      style={{
                        padding: '6px 10px',
                        fontSize: '13px',
                        backgroundColor: '#ffc107',
                        color: '#333',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        fontWeight: '600',
                        flexShrink: 0
                      }}
                      title="즐겨찾기에 추가"
                    >
                      L
                    </button>
                  )
                )}
                {/* 관심종목 추가 버튼 */}
                {selectedStock && !watchlist.find(item => item.symbol === selectedStock) && (
                  <button
                    onClick={async () => {
                      if (!window.confirm(`${selectedStock} (${stockData?.name || '종목명 없음'})을(를) 관심종목에 추가하시겠습니까?`)) {
                        return;
                      }
                      try {
                        const response = await authenticatedFetch('/api/watchlist', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            symbol: selectedStock,
                            stock_name: stockData?.name || '',
                            reason: '종목코드 검색으로 추가',
                            is_ai_recommended: false // 수동 추가
                          })
                        });
                        
                        if (response.ok) {
                          alert('관심종목에 추가되었습니다.');
                          await fetchWatchlist();
                        } else {
                          const error = await response.json();
                          alert(error.error || '관심종목 추가에 실패했습니다.');
                        }
                      } catch (error) {
                        console.error('관심종목 추가 실패:', error);
                        alert('관심종목 추가 중 오류가 발생했습니다.');
                      }
                    }}
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      backgroundColor: '#17a2b8',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      fontWeight: '500',
                      flexShrink: 0
                    }}
                    title="관심종목에 추가 (수동 추가)"
                  >
                    📝 관심종목 추가
                  </button>
                )}
              </div>
            </div>

            {/* 주식 상세 정보 - stockData가 있고 price가 있을 때만 표시 */}
            {stockData && stockData.price !== undefined && stockData.price !== null && (
              <>
                <div className="stock-price">
                  <span className="price">₩{parseInt(stockData.price || 0).toLocaleString()}</span>
                  <span className={`change ${(stockData.change || 0) >= 0 ? 'positive' : 'negative'}`}>
                    {stockData.changeRate !== undefined 
                      ? formatChange(stockData)
                      : 'N/A'}
                  </span>
                </div>
                <div className="stock-details">
                  <div>시가: ₩{parseInt(stockData.open || 0).toLocaleString()}</div>
                  <div>고가: ₩{parseInt(stockData.high || 0).toLocaleString()}</div>
                  <div>저가: ₩{parseInt(stockData.low || 0).toLocaleString()}</div>
                  <div>거래량: {parseInt(stockData.volume || 0).toLocaleString()}</div>
                </div>
              </>
            )}
          </div>
        )}

        <div className="order-panel">
          <h2>주문</h2>
          
          {/* 매수/매도 선택 */}
          <div className="order-type">
            <button 
              className={orderType === 'buy' ? 'active buy' : ''}
              onClick={() => setOrderType('buy')}
            >
              매수
            </button>
            <button 
              className={orderType === 'sell' ? 'active sell' : ''}
              onClick={() => setOrderType('sell')}
            >
              매도
            </button>
          </div>

          {/* 주문 방법 선택 */}
          <div className="order-method">
            <label>
              <input
                type="radio"
                value="limit"
                checked={orderMethod === 'limit'}
                onChange={(e) => setOrderMethod(e.target.value)}
              />
              지정가
            </label>
            <label>
              <input
                type="radio"
                value="market"
                checked={orderMethod === 'market'}
                onChange={(e) => setOrderMethod(e.target.value)}
              />
              시장가
            </label>
          </div>

          <div className="order-form">
            <div className="form-group">
              <label htmlFor="order_quantity">수량 (주)</label>
              <input
                id="order_quantity"
                name="order_quantity"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="주식 수량"
                min="1"
              />
            </div>

            {orderMethod === 'limit' && (
              <div className="form-group">
                <label htmlFor="order_price">가격 (원)</label>
                <input
                  id="order_price"
                  name="order_price"
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="주문 가격"
                  step="1"
                  min="0"
                />
              </div>
            )}

            {orderMethod === 'market' && (
              <div className="form-group">
                <label htmlFor="order_price_market">가격 (원)</label>
                <input
                  id="order_price_market"
                  name="order_price_market"
                  type="text"
                  value={stockData && stockData.price ? `₩${parseInt(stockData.price).toLocaleString()} (시장가)` : '시장가'}
                  disabled
                  className="market-price"
                />
              </div>
            )}

            {quantity && price && orderMethod === 'limit' && (
              <div className="order-summary">
                <div>총 주문금액: ₩{(parseInt(quantity) * parseFloat(price)).toLocaleString()}</div>
              </div>
            )}

            {quantity && stockData?.price && orderMethod === 'market' && (
              <div className="order-summary">
                <div>예상 주문금액: ₩{(parseInt(quantity) * parseFloat(stockData.price)).toLocaleString()}</div>
                <small>(시장가 기준, 실제 체결가와 다를 수 있음)</small>
              </div>
            )}

            <button 
              className={`btn-order ${orderType}`}
              onClick={executeOrder}
              disabled={loading || !quantity || (orderMethod === 'limit' && !price) || authError}
            >
              {loading ? '처리 중...' : `${orderType === 'buy' ? '매수' : '매도'} 주문`}
            </button>
          </div>
        </div>
      </div>

      {/* 오른쪽: AI 분석 */}
      <div className="trading-main">
        {/* AI 거래 분석 종목 표시 (선택된 종목이 AI 분석 대상인 경우) - useEffect로 자동 연동 */}
        
        {aiAnalysis && (
          <div className="ai-analysis-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <h2 style={{ margin: 0 }}>🤖 AI 분석 결과</h2>
                {/* 분석 일자 표시 */}
                {aiAnalysis.analysisDate && (
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#666',
                    fontWeight: 'normal'
                  }}>
                    📅 분석 일자: {aiAnalysis.analysisDate}
                    {aiAnalysis.isExisting && (
                      <span style={{ 
                        marginLeft: '8px', 
                        padding: '2px 6px', 
                        backgroundColor: '#fff3cd', 
                        color: '#856404',
                        borderRadius: '3px',
                        fontSize: '11px'
                      }}>
                        기존 분석
                      </span>
                    )}
                  </div>
                )}
              </div>
              {/* AI 거래 분석 배지 */}
              {selectedStock && aiAnalyses && aiAnalyses.find(a => a.symbol === selectedStock) && (
                <span style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  backgroundColor: '#667eea',
                  color: 'white',
                  borderRadius: '4px',
                  fontWeight: 'bold'
                }}>
                  AI 거래 분석 종목
                </span>
              )}
              <div style={{ display: 'flex', gap: '10px' }}>
                {/* 분석 업데이트 버튼 */}
                {updateAiAnalysis && (
                  <button
                    onClick={updateAiAnalysis}
                    disabled={loading || !selectedStock}
                    style={{
                      padding: '8px 16px',
                      fontSize: '14px',
                      backgroundColor: loading ? '#ccc' : '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: loading || !selectedStock ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      transition: 'background-color 0.2s',
                      opacity: loading || !selectedStock ? 0.6 : 1
                    }}
                    onMouseOver={(e) => {
                      if (!loading && selectedStock) {
                        e.target.style.backgroundColor = '#0056b3';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!loading && selectedStock) {
                        e.target.style.backgroundColor = '#007bff';
                      }
                    }}
                    title="현재 옵션으로 분석을 업데이트합니다"
                  >
                    {loading ? '분석 중...' : '🔄 분석 업데이트'}
                  </button>
                )}
                {/* 분석 초기화 버튼 */}
                <button
                  onClick={resetAiAnalysis}
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
                >
                  🔄 분석초기화
                </button>
                {/* AI 거래 분석 리스트와 동일한 상세 모달 열기 */}
                {aiAnalyses && selectedStock && aiAnalyses.some(a => (a.symbol || '').toString().trim() === (selectedStock || '').toString().trim()) && setSelectedAnalysis && setShowAnalysisDetail && setActiveTab && (
                  <button
                    onClick={() => {
                      const matches = aiAnalyses.filter(a => (a.symbol || '').toString().trim() === (selectedStock || '').toString().trim());
                      const currentAnalysis = matches.length
                        ? matches.sort((a, b) => new Date(b.createdAt || b.updatedAt || 0) - new Date(a.createdAt || a.updatedAt || 0))[0]
                        : null;
                      if (currentAnalysis) {
                        setSelectedAnalysis(currentAnalysis);
                        setShowAnalysisDetail(true);
                        setActiveTab('ai-trade');
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      fontSize: '14px',
                      backgroundColor: '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => { e.target.style.backgroundColor = '#5a67d8'; }}
                    onMouseOut={(e) => { e.target.style.backgroundColor = '#667eea'; }}
                    title="AI 거래설정의 분석 리스트에서 보는 것과 동일한 상세 모달을 엽니다"
                  >
                    📋 상세 보기
                  </button>
                )}
              </div>
            </div>
            
            {/* 분석 메타데이터 (상단 표시) */}
            {aiAnalysis && aiAnalysis.analysisMetadata && (
              <div className="analysis-metadata">
                {/* 데이터 검증 경고 */}
                {aiAnalysis.analysisMetadata?.data && 
                 aiAnalysis.analysisMetadata.data.데이터검증 && 
                 aiAnalysis.analysisMetadata.data.데이터검증.상태 &&
                 !aiAnalysis.analysisMetadata.data.데이터검증.상태.includes('정상') && (
                  <div className="data-validation-warning">
                    <h3>⚠️ 데이터 검증 경고</h3>
                    <div className="validation-status">
                      <strong>상태:</strong> {aiAnalysis.analysisMetadata.data.데이터검증.상태}
                    </div>
                    {aiAnalysis.analysisMetadata?.data?.데이터검증?.이슈 && Array.isArray(aiAnalysis.analysisMetadata.data.데이터검증.이슈) && (
                    <div className="validation-issues">
                      <strong>발견된 이슈:</strong>
                      <ul>
                        {aiAnalysis.analysisMetadata.data.데이터검증.이슈.map((issue, idx) => (
                          <li key={idx}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                    )}
                    <div className="validation-note">
                      ⚠️ 비정상적인 데이터로 인해 분석 결과의 신뢰도가 낮을 수 있습니다.
                    </div>
                  </div>
                )}
                
                <div className="metadata-section">
                  <h3>📊 분석 도구</h3>
                  <div className="metadata-tags">
                    {aiAnalysis.analysisMetadata?.tools && Array.isArray(aiAnalysis.analysisMetadata.tools) ? (
                      aiAnalysis.analysisMetadata.tools.map((tool, index) => (
                      <span key={index} className="metadata-tag">{tool}</span>
                      ))
                    ) : (
                      <span className="metadata-tag">분석 도구 정보 없음</span>
                    )}
                  </div>
                </div>
                
                <div className="metadata-section">
                  <h3>📈 분석 데이터</h3>
                  {!(aiAnalysis.analysisMetadata?.data && (aiAnalysis.analysisMetadata.data.기본시세 || aiAnalysis.analysisMetadata.data.기술적지표 || aiAnalysis.analysisMetadata.data.포트폴리오)) && (
                    <div className="scope-item" style={{ color: '#6c757d', fontSize: '13px' }}>단일 종목 분석 (시세·지표는 분석 시점 기준)</div>
                  )}
                  {aiAnalysis.analysisMetadata?.data && aiAnalysis.analysisMetadata.data.기본시세 && (
                    <div className="data-group">
                      <strong>기본 시세:</strong>
                      <div className="data-items">
                        {Object.entries(aiAnalysis.analysisMetadata.data.기본시세).map(([key, value]) => (
                          <span key={key} className="data-item">{key}: {value}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {aiAnalysis.analysisMetadata.data && aiAnalysis.analysisMetadata.data.기술적지표 && (
                    <div className="data-group">
                      <strong>기술적 지표:</strong>
                      <div className="data-items">
                        {Object.entries(aiAnalysis.analysisMetadata.data.기술적지표).map(([key, value]) => (
                          <span key={key} className="data-item">{key}: {value}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {aiAnalysis.analysisMetadata.data && aiAnalysis.analysisMetadata.data.포트폴리오 && (
                    <div className="data-group" style={{ marginBottom: '20px' }}>
                      <strong>포트폴리오:</strong>
                      <div className="data-items" style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
                        {Object.entries(aiAnalysis.analysisMetadata.data.포트폴리오).map(([key, value]) => {
                          const itemId = `포트폴리오_${key}`;
                          const itemExplanation = itemExplanations[itemId];
                          const isLoading = itemExplanationLoading[itemId];
                          return (
                            <div key={key} style={{
                              padding: '12px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '6px',
                              border: '1px solid #dee2e6'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: itemExplanation ? '8px' : '0' }}>
                                <span className="data-item" style={{ fontWeight: 'bold', color: '#495057' }}>
                                  {key}: <span style={{ color: '#6c757d' }}>{value}</span>
                                </span>
                                <button
                                  onClick={() => generateItemExplanation(key, value, '포트폴리오')}
                                  disabled={isLoading || !!itemExplanation}
                                  style={{
                                    padding: '4px 12px',
                                    fontSize: '12px',
                                    backgroundColor: isLoading || itemExplanation ? '#ccc' : '#17a2b8',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: isLoading || itemExplanation ? 'not-allowed' : 'pointer',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {isLoading ? '해석 중...' : itemExplanation ? '✓ 해석됨' : '📚 해석'}
                                </button>
                              </div>
                              {itemExplanation && (
                                <div style={{
                                  marginTop: '8px',
                                  padding: '10px',
                                  backgroundColor: '#fff3cd',
                                  borderRadius: '4px',
                                  borderLeft: '3px solid #ffc107',
                                  fontSize: '13px',
                                  color: '#856404',
                                  lineHeight: '1.6',
                                  whiteSpace: 'pre-wrap'
                                }}>
                                  💡 {itemExplanation.explanation}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {aiAnalysis.analysisMetadata.marketContext && (
                    <div className="data-group" style={{ marginBottom: '20px' }}>
                      <strong>시장 컨텍스트:</strong>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '10px' }}>
                        {/* 뉴스 */}
                        {aiAnalysis.analysisMetadata.marketContext.news && aiAnalysis.analysisMetadata.marketContext.news.length > 0 && (
                          <div>
                            <strong style={{ color: '#495057', marginBottom: '8px', display: 'block' }}>📰 최근 뉴스:</strong>
                            {aiAnalysis.analysisMetadata.marketContext.news.slice(0, 3).map((news, idx) => {
                              const itemId = `뉴스_${idx}`;
                              const itemExplanation = itemExplanations[itemId];
                              const isLoading = itemExplanationLoading[itemId];
                              return (
                                <div key={idx} style={{
                                  padding: '12px',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '6px',
                                  border: '1px solid #dee2e6',
                                  marginBottom: '8px'
                                }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: itemExplanation ? '8px' : '0' }}>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontWeight: 'bold', color: '#495057', marginBottom: '4px' }}>
                                        {news.title}
                                      </div>
                                      <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                        {news.source} · {new Date(news.date).toLocaleDateString('ko-KR')}
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => generateItemExplanation(news.title, news.title, '뉴스')}
                                      disabled={isLoading || !!itemExplanation}
                                      style={{
                                        padding: '4px 12px',
                                        fontSize: '12px',
                                        backgroundColor: isLoading || itemExplanation ? '#ccc' : '#17a2b8',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: isLoading || itemExplanation ? 'not-allowed' : 'pointer',
                                        whiteSpace: 'nowrap',
                                        marginLeft: '10px'
                                      }}
                                    >
                                      {isLoading ? '해석 중...' : itemExplanation ? '✓ 해석됨' : '📚 해석'}
                                    </button>
                                  </div>
                                  {itemExplanation && (
                                    <div style={{
                                      marginTop: '8px',
                                      padding: '10px',
                                      backgroundColor: '#fff3cd',
                                      borderRadius: '4px',
                                      borderLeft: '3px solid #ffc107',
                                      fontSize: '13px',
                                      color: '#856404',
                                      lineHeight: '1.6',
                                      whiteSpace: 'pre-wrap'
                                    }}>
                                      💡 {itemExplanation.explanation}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {/* 금리 정보 */}
                        {aiAnalysis.analysisMetadata.marketContext.interestRates && (
                          <div>
                            <strong style={{ color: '#495057', marginBottom: '8px', display: 'block' }}>💰 금리 정보:</strong>
                            {aiAnalysis.analysisMetadata.marketContext.interestRates.us && (
                              <div style={{
                                padding: '12px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '6px',
                                border: '1px solid #dee2e6',
                                marginBottom: '8px'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: itemExplanations['금리_미국 연준 금리'] ? '8px' : '0' }}>
                                  <span style={{ fontWeight: 'bold', color: '#495057' }}>
                                    미국 연준 금리: <span style={{ color: '#007bff' }}>{aiAnalysis.analysisMetadata.marketContext.interestRates.us.rate}%</span>
                                  </span>
                                  <button
                                    onClick={() => generateItemExplanation('미국 연준 금리', `${aiAnalysis.analysisMetadata.marketContext.interestRates.us.rate}%`, '금리')}
                                    disabled={itemExplanationLoading['금리_미국 연준 금리'] || !!itemExplanations['금리_미국 연준 금리']}
                                    style={{
                                      padding: '4px 12px',
                                      fontSize: '12px',
                                      backgroundColor: itemExplanationLoading['금리_미국 연준 금리'] || itemExplanations['금리_미국 연준 금리'] ? '#ccc' : '#17a2b8',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: itemExplanationLoading['금리_미국 연준 금리'] || itemExplanations['금리_미국 연준 금리'] ? 'not-allowed' : 'pointer',
                                      whiteSpace: 'nowrap',
                                      marginLeft: '10px'
                                    }}
                                  >
                                    {itemExplanationLoading['금리_미국 연준 금리'] ? '해석 중...' : itemExplanations['금리_미국 연준 금리'] ? '✓ 해석됨' : '📚 해석'}
                                  </button>
                                </div>
                                {itemExplanations['금리_미국 연준 금리'] && (
                                  <div style={{
                                    marginTop: '8px',
                                    padding: '10px',
                                    backgroundColor: '#fff3cd',
                                    borderRadius: '4px',
                                    borderLeft: '3px solid #ffc107',
                                    fontSize: '13px',
                                    color: '#856404',
                                    lineHeight: '1.6',
                                    whiteSpace: 'pre-wrap'
                                  }}>
                                    💡 {itemExplanations['금리_미국 연준 금리'].explanation}
                                  </div>
                                )}
                              </div>
                            )}
                            {aiAnalysis.analysisMetadata.marketContext.interestRates.korea && (
                              <div style={{
                                padding: '12px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '6px',
                                border: '1px solid #dee2e6',
                                marginBottom: '8px'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: itemExplanations['금리_한국 기준금리'] ? '8px' : '0' }}>
                                  <span style={{ fontWeight: 'bold', color: '#495057' }}>
                                    한국 기준금리: <span style={{ color: '#007bff' }}>{aiAnalysis.analysisMetadata.marketContext.interestRates.korea.rate}%</span>
                                  </span>
                                  <button
                                    onClick={() => generateItemExplanation('한국 기준금리', `${aiAnalysis.analysisMetadata.marketContext.interestRates.korea.rate}%`, '금리')}
                                    disabled={itemExplanationLoading['금리_한국 기준금리'] || !!itemExplanations['금리_한국 기준금리']}
                                    style={{
                                      padding: '4px 12px',
                                      fontSize: '12px',
                                      backgroundColor: itemExplanationLoading['금리_한국 기준금리'] || itemExplanations['금리_한국 기준금리'] ? '#ccc' : '#17a2b8',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: itemExplanationLoading['금리_한국 기준금리'] || itemExplanations['금리_한국 기준금리'] ? 'not-allowed' : 'pointer',
                                      whiteSpace: 'nowrap',
                                      marginLeft: '10px'
                                    }}
                                  >
                                    {itemExplanationLoading['금리_한국 기준금리'] ? '해석 중...' : itemExplanations['금리_한국 기준금리'] ? '✓ 해석됨' : '📚 해석'}
                                  </button>
                                </div>
                                {itemExplanations['금리_한국 기준금리'] && (
                                  <div style={{
                                    marginTop: '8px',
                                    padding: '10px',
                                    backgroundColor: '#fff3cd',
                                    borderRadius: '4px',
                                    borderLeft: '3px solid #ffc107',
                                    fontSize: '13px',
                                    color: '#856404',
                                    lineHeight: '1.6',
                                    whiteSpace: 'pre-wrap'
                                  }}>
                                    💡 {itemExplanations['금리_한국 기준금리'].explanation}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        {/* 정책 발표 */}
                        {aiAnalysis.analysisMetadata.marketContext.policies && aiAnalysis.analysisMetadata.marketContext.policies.length > 0 && (
                          <div>
                            <strong style={{ color: '#495057', marginBottom: '8px', display: 'block' }}>📋 최근 정책 발표:</strong>
                            {aiAnalysis.analysisMetadata.marketContext.policies.slice(0, 2).map((policy, idx) => {
                              const itemId = `정책_${idx}`;
                              const itemExplanation = itemExplanations[itemId];
                              const isLoading = itemExplanationLoading[itemId];
                              return (
                                <div key={idx} style={{
                                  padding: '12px',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '6px',
                                  border: '1px solid #dee2e6',
                                  marginBottom: '8px'
                                }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: itemExplanation ? '8px' : '0' }}>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontWeight: 'bold', color: '#495057', marginBottom: '4px' }}>
                                        {policy.title}
                                      </div>
                                      <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                        {policy.source} · {new Date(policy.date).toLocaleDateString('ko-KR')}
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => generateItemExplanation(policy.title, policy.title, '정책')}
                                      disabled={isLoading || !!itemExplanation}
                                      style={{
                                        padding: '4px 12px',
                                        fontSize: '12px',
                                        backgroundColor: isLoading || itemExplanation ? '#ccc' : '#17a2b8',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: isLoading || itemExplanation ? 'not-allowed' : 'pointer',
                                        whiteSpace: 'nowrap',
                                        marginLeft: '10px'
                                      }}
                                    >
                                      {isLoading ? '해석 중...' : itemExplanation ? '✓ 해석됨' : '📚 해석'}
                                    </button>
                                  </div>
                                  {itemExplanation && (
                                    <div style={{
                                      marginTop: '8px',
                                      padding: '10px',
                                      backgroundColor: '#fff3cd',
                                      borderRadius: '4px',
                                      borderLeft: '3px solid #ffc107',
                                      fontSize: '13px',
                                      color: '#856404',
                                      lineHeight: '1.6',
                                      whiteSpace: 'pre-wrap'
                                    }}>
                                      💡 {itemExplanation.explanation}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="metadata-section">
                  <h3>🔍 분석 범위</h3>
                  <div className="scope-items">
                    {aiAnalysis.analysisMetadata?.scope && typeof aiAnalysis.analysisMetadata.scope === 'object' ? (
                      Object.entries(aiAnalysis.analysisMetadata.scope).map(([key, value]) => (
                      <div key={key} className="scope-item">
                        <strong>{key}:</strong> {value}
                      </div>
                      ))
                    ) : (
                      <div className="scope-item">분석 범위 정보 없음</div>
                    )}
                  </div>
                </div>
                
                <div className="metadata-section confidence-section">
                  <h3>신뢰도: {aiAnalysis.analysisMetadata?.confidence || 0}%</h3>
                  <div className="confidence-bar">
                    <div 
                      className="confidence-fill" 
                      style={{ width: `${aiAnalysis.analysisMetadata?.confidence || 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
            
            {/* AI 분석 오류 정보 */}
            {aiAnalysis.hasError && aiAnalysis.analysisMetadata?.errorInfo && (
              <div style={{
                marginBottom: '20px',
                padding: '15px',
                backgroundColor: '#fff3cd',
                border: '2px solid #ffc107',
                borderRadius: '8px'
              }}>
                <h3 style={{ marginTop: 0, color: '#856404', fontSize: '16px' }}>
                  ⚠️ AI 분석 오류
                </h3>
                <div style={{ marginBottom: '10px' }}>
                  <strong>원인:</strong> {aiAnalysis.analysisMetadata.errorInfo.reason}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong>상세:</strong> {aiAnalysis.analysisMetadata.errorInfo.details}
                </div>
                <div style={{ 
                  padding: '10px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '5px',
                  whiteSpace: 'pre-line',
                  fontSize: '13px'
                }}>
                  <strong>해결 방법:</strong>
                  <div style={{ marginTop: '5px' }}>
                    {aiAnalysis.analysisMetadata.errorInfo.solution}
                  </div>
                </div>
              </div>
            )}
            
            {/* 분석 내용 */}
            <div className="analysis-content">
              <div className="analysis-recommendation" style={{
                whiteSpace: 'pre-line',
                lineHeight: '1.6',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                {(() => {
                  // 1. recommendation이 객체인 경우
                  if (aiAnalysis.recommendation && typeof aiAnalysis.recommendation === 'object') {
                    const reasoning = aiAnalysis.recommendation.reasoning;
                    
                    // reasoning이 있으면 사용
                    if (reasoning) {
                      if (Array.isArray(reasoning)) {
                        return reasoning.join('\n');
                      }
                      if (typeof reasoning === 'string' && reasoning.trim()) {
                        return reasoning;
                      }
                    }
                    
                    // reasoning이 없으면 recommendation 객체를 포맷팅하여 표시
                    const action = aiAnalysis.recommendation.action || 'none';
                    const actionText = action === 'buy' ? '매수' : action === 'sell' ? '매도' : action === 'hold' ? '보유' : '분석 완료';
                    const confidence = aiAnalysis.recommendation.confidence || aiAnalysis.recommendation.confidence_score || 0;
                    const targetPrice = aiAnalysis.recommendation.target_price || 0;
                    const stopLossPrice = aiAnalysis.recommendation.stop_loss_price || 0;
                    
                    let formattedText = `📊 AI 추천: ${actionText}\n`;
                    formattedText += `신뢰도: ${confidence}%\n`;
                    if (targetPrice > 0) {
                      formattedText += `목표가: ₩${targetPrice.toLocaleString()}\n`;
                    }
                    if (stopLossPrice > 0) {
                      formattedText += `손절가: ₩${stopLossPrice.toLocaleString()}\n`;
                    }
                    if (reasoning && typeof reasoning === 'string') {
                      formattedText += `\n이유:\n${reasoning}`;
                    }
                    
                    return formattedText;
                  }
                  
                  // 2. recommendation이 문자열인 경우
                  if (typeof aiAnalysis.recommendation === 'string') {
                    return aiAnalysis.recommendation;
                  }
                  
                  // 3. analysis 필드 사용
                  if (aiAnalysis.analysis) {
                    return aiAnalysis.analysis;
                  }
                  
                  // 4. fallback: 기본 메시지
                  return 'AI 분석 결과를 불러오는 중입니다...';
                })()}
              </div>
              {/* 신뢰도 표시 (analysisMetadata가 없는 경우) */}
              {aiAnalysis.confidence && !aiAnalysis.analysisMetadata && (
                <div className="analysis-confidence" style={{
                  marginTop: '15px',
                  padding: '10px',
                  backgroundColor: '#e7f3ff',
                  borderRadius: '6px',
                  border: '1px solid #b3d9ff',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  color: '#004085'
                }}>
                  신뢰도: {aiAnalysis.confidence}%
                  {aiAnalysis.recommendation && typeof aiAnalysis.recommendation === 'object' && (
                    <div style={{ marginTop: '8px', fontSize: '14px', fontWeight: 'normal' }}>
                      추천: {
                        aiAnalysis.recommendation.action === 'buy' ? '매수' :
                        aiAnalysis.recommendation.action === 'sell' ? '매도' :
                        aiAnalysis.recommendation.action === 'hold' ? '보유' :
                        '분석 완료'
                      }
                    </div>
                  )}
                </div>
              )}
              
              {/* 해석 출력 버튼 */}
              <div style={{ marginTop: '15px', textAlign: 'center' }}>
                <button
                  onClick={() => {
                    const textToExplain = (() => {
                      if (aiAnalysis.recommendation && typeof aiAnalysis.recommendation === 'object') {
                        return aiAnalysis.recommendation.reasoning || JSON.stringify(aiAnalysis.recommendation);
                      }
                      return aiAnalysis.recommendation || aiAnalysis.analysis;
                    })();
                    generateSimpleExplanation(textToExplain);
                  }}
                  disabled={explanationLoading}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    backgroundColor: explanationLoading ? '#ccc' : '#17a2b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: explanationLoading ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                >
                  {explanationLoading ? '해석 생성 중...' : '📚 해석 출력'}
                </button>
              </div>
              
              {/* 쉬운 해설 표시 */}
              {simpleExplanation && (() => {
                const textToCompare = (() => {
                  if (aiAnalysis.recommendation && typeof aiAnalysis.recommendation === 'object') {
                    return aiAnalysis.recommendation.reasoning || JSON.stringify(aiAnalysis.recommendation);
                  }
                  return aiAnalysis.recommendation || aiAnalysis.analysis;
                })();
                return simpleExplanation.originalText === textToCompare;
              })() && (
                <div className="simple-explanation" style={{
                  marginTop: '20px',
                  padding: '20px',
                  backgroundColor: '#fff3cd',
                  borderRadius: '8px',
                  border: '2px solid #ffc107',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <h4 style={{ 
                    marginTop: 0, 
                    marginBottom: '15px',
                    color: '#856404',
                    fontSize: '18px',
                    fontWeight: 'bold'
                  }}>
                    📚 초등학생도 이해하는 쉬운 해설
                  </h4>
                  <div style={{
                    color: '#856404',
                    lineHeight: '1.8',
                    fontSize: '15px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {simpleExplanation.explanation}
                  </div>
                  {simpleExplanation.timestamp && (
                    <div style={{ 
                      marginTop: '15px', 
                      fontSize: '12px', 
                      color: '#856404',
                      textAlign: 'right',
                      fontStyle: 'italic'
                    }}>
                      생성 시간: {new Date(simpleExplanation.timestamp).toLocaleString('ko-KR')}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 질의 응답 섹션 */}
            <div className="ai-question-section" style={{ marginTop: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
              <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '16px' }}>💬 분석 결과 질문하기</h3>
              
              {/* 질의 응답 입력 */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <input
                  type="text"
                  value={aiQuestion}
                  onChange={(e) => setAiQuestion(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !aiQuestionLoading && aiQuestion.trim()) {
                      askAiQuestion();
                    }
                  }}
                  placeholder="분석 결과에 대해 질문하세요 (예: 이 종목의 투자 가치가 있나요?)"
                  style={{
                    flex: 1,
                    padding: '10px',
                    fontSize: '14px',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                  disabled={aiQuestionLoading}
                />
                <button
                  onClick={askAiQuestion}
                  disabled={aiQuestionLoading || !aiQuestion.trim()}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    backgroundColor: aiQuestionLoading || !aiQuestion.trim() ? '#ccc' : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: aiQuestionLoading || !aiQuestion.trim() ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {aiQuestionLoading ? '응답 중...' : '질문하기'}
                </button>
              </div>

              {/* 질의 응답 결과 */}
              {aiAnswer && (
                <div className="ai-answer" style={{
                  padding: '15px',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  border: '1px solid #e9ecef',
                  marginTop: '10px'
                }}>
                  <div style={{ marginBottom: '10px', color: '#666', fontSize: '13px' }}>
                    <strong>질문:</strong> {aiAnswer.question}
                  </div>
                  <div style={{ color: '#333', lineHeight: '1.6', fontSize: '14px' }}>
                    <strong>답변:</strong> {aiAnswer.answer}
                  </div>
                  {aiAnswer.timestamp && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#999' }}>
                      {new Date(aiAnswer.timestamp).toLocaleString('ko-KR')}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="ai-analysis-section">
          <h2>AI 분석</h2>
          
          {/* AI 분석 옵션 */}
          <div style={{ 
            marginBottom: '15px', 
            padding: '15px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            border: '1px solid #dee2e6'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '16px', fontWeight: 'bold' }}>
              AI 분석 옵션
            </h3>
            
            {/* 로컬 AI 체크박스 */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={aiAnalysisOptions.useLocalAI}
                  onChange={(e) => setAiAnalysisOptions({
                    ...aiAnalysisOptions,
                    useLocalAI: e.target.checked
                  })}
                  style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                  로컬 AI (Ollama) - 기본
                </span>
              </label>
              <small style={{ display: 'block', marginLeft: '26px', color: '#666', fontSize: '12px' }}>
                빠르고 무료, 기본 분석 제공
              </small>
            </div>
            
            {/* Google AI 체크박스 */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={aiAnalysisOptions.useGoogleAI}
                  onChange={(e) => setAiAnalysisOptions({
                    ...aiAnalysisOptions,
                    useGoogleAI: e.target.checked
                  })}
                  style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                  Google AI (Gemini) - 유료
                </span>
              </label>
              <small style={{ display: 'block', marginLeft: '26px', color: '#666', fontSize: '12px' }}>
                심층 분석 및 웹 검색 포함 (시간이 더 걸릴 수 있음)
              </small>
            </div>
            
            {/* Google AI 모델 선택 (Google AI가 활성화된 경우만 표시) */}
            {aiAnalysisOptions.useGoogleAI && (
              <div style={{ marginLeft: '26px', marginTop: '10px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 'bold' }}>
                  Google AI 모델 선택:
                </label>
                <select
                  value={aiAnalysisOptions.googleAIModel}
                  onChange={(e) => setAiAnalysisOptions({
                    ...aiAnalysisOptions,
                    googleAIModel: e.target.value
                  })}
                  style={{
                    width: '100%',
                    padding: '8px',
                    fontSize: '13px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="gemini-2.5-flash">Gemini 2.5 Flash (기본, 빠름, 저렴)</option>
                  <option value="gemini-3-flash-preview">Gemini 3 Flash Preview (최신, 빠름)</option>
                  <option value="gemini-2.5-pro">Gemini 2.5 Pro (고품질, 느림, 비쌈)</option>
                </select>
                <small style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '11px' }}>
                  {aiAnalysisOptions.googleAIModel === 'gemini-2.5-flash' && '⚡ 빠르고 비용 효율적 (권장)'}
                  {aiAnalysisOptions.googleAIModel === 'gemini-3-flash-preview' && '🚀 최신 모델, 빠름'}
                  {aiAnalysisOptions.googleAIModel === 'gemini-2.5-pro' && '💎 고품질 분석, 느리고 비쌈'}
                </small>
              </div>
            )}
            
            {/* 옵션 안내 */}
            <div style={{ 
              marginTop: '12px', 
              padding: '10px', 
              backgroundColor: '#e7f3ff', 
              borderRadius: '4px',
              fontSize: '12px',
              color: '#004085'
            }}>
              <strong>💡 안내:</strong>
              <ul style={{ margin: '6px 0 0 0', paddingLeft: '20px' }}>
                <li>로컬 AI만 사용: 빠르고 무료 (약 2-5초)</li>
                <li>로컬 AI + Google AI: 심층 분석 (약 10-30초, 유료)</li>
                <li>Google AI만 사용: 가능하지만 로컬 AI와 함께 사용 권장</li>
              </ul>
            </div>
          </div>
          
          <button 
            className="btn-ai-analysis" 
            onClick={requestAiAnalysis}
            disabled={!selectedStock || loading || (!aiAnalysisOptions.useLocalAI && !aiAnalysisOptions.useGoogleAI)}
            style={{
              opacity: (!selectedStock || loading || (!aiAnalysisOptions.useLocalAI && !aiAnalysisOptions.useGoogleAI)) ? 0.6 : 1,
              cursor: (!selectedStock || loading || (!aiAnalysisOptions.useLocalAI && !aiAnalysisOptions.useGoogleAI)) ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? '분석 중...' : '🤖 AI 분석 요청'}
          </button>

          {/* 단답형 질의 응답 (항상 사용 가능) */}
          <div className="ai-quick-question-section" style={{ 
            marginTop: '20px', 
            padding: '15px', 
            border: '2px solid #007bff', 
            borderRadius: '8px', 
            backgroundColor: '#f0f8ff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ 
              marginTop: 0, 
              marginBottom: '15px', 
              fontSize: '18px',
              color: '#007bff',
              fontWeight: 'bold'
            }}>
              💬 빠른 질의 응답
            </h3>
            
            {/* 질의 응답 입력 */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
              <input
                type="text"
                value={aiQuestion}
                onChange={(e) => setAiQuestion(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !aiQuestionLoading && aiQuestion.trim()) {
                    askAiQuestion();
                  }
                }}
                placeholder={selectedStock 
                  ? "종목이나 주식에 대해 질문하세요 (예: 이 종목의 투자 가치가 있나요?)"
                  : "주식에 대해 질문하세요 (예: 주식 투자 전략은?)"}
                style={{
                  flex: 1,
                  padding: '10px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
                disabled={aiQuestionLoading}
              />
              <button
                onClick={askAiQuestion}
                disabled={aiQuestionLoading || !aiQuestion.trim()}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  backgroundColor: aiQuestionLoading || !aiQuestion.trim() ? '#ccc' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: aiQuestionLoading || !aiQuestion.trim() ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                {aiQuestionLoading ? '응답 중...' : '질문하기'}
              </button>
            </div>

              {/* 질의 응답 결과 */}
              {aiAnswer && (
                <div className="ai-answer" style={{
                  padding: '15px',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  border: '1px solid #e9ecef',
                  marginTop: '10px',
                  minHeight: '50px'
                }}>
                  <div style={{ marginBottom: '10px', color: '#666', fontSize: '13px', fontWeight: 'bold' }}>
                    <span style={{ color: '#007bff' }}>❓ 질문:</span> {aiAnswer.question}
                  </div>
                  <div style={{ 
                    color: '#333', 
                    lineHeight: '1.8', 
                    fontSize: '14px', 
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    padding: '10px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    borderLeft: '3px solid #28a745'
                  }}>
                    <span style={{ color: '#28a745', fontWeight: 'bold' }}>💡 답변:</span> {aiAnswer.answer}
                  </div>
                  
                  {/* 답변에 대한 해석 출력 버튼 */}
                  <div style={{ marginTop: '15px', textAlign: 'center' }}>
                    <button
                      onClick={() => generateSimpleExplanation(aiAnswer.answer)}
                      disabled={explanationLoading}
                      style={{
                        padding: '8px 16px',
                        fontSize: '13px',
                        backgroundColor: explanationLoading ? '#ccc' : '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: explanationLoading ? 'not-allowed' : 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      {explanationLoading ? '해석 생성 중...' : '📚 답변 해석 출력'}
                    </button>
                  </div>
                  
                  {/* 답변에 대한 쉬운 해설 표시 */}
                  {simpleExplanation && simpleExplanation.originalText === aiAnswer.answer && (
                    <div className="simple-explanation" style={{
                      marginTop: '15px',
                      padding: '15px',
                      backgroundColor: '#fff3cd',
                      borderRadius: '6px',
                      border: '2px solid #ffc107'
                    }}>
                      <h5 style={{ 
                        marginTop: 0, 
                        marginBottom: '10px',
                        color: '#856404',
                        fontSize: '16px',
                        fontWeight: 'bold'
                      }}>
                        📚 쉬운 해설
                      </h5>
                      <div style={{
                        color: '#856404',
                        lineHeight: '1.6',
                        fontSize: '14px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {simpleExplanation.explanation}
                      </div>
                    </div>
                  )}
                  
                  {aiAnswer.timestamp && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#999', textAlign: 'right' }}>
                      {new Date(aiAnswer.timestamp).toLocaleString('ko-KR')}
                    </div>
                  )}
                </div>
              )}
            
            {/* 답변이 없을 때 안내 메시지 */}
            {!aiAnswer && !aiQuestionLoading && (
              <div style={{ 
                padding: '10px', 
                color: '#666', 
                fontSize: '13px', 
                textAlign: 'center',
                fontStyle: 'italic'
              }}>
                위 입력란에 질문을 입력하고 "질문하기" 버튼을 클릭하세요.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradeTab;

